import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { CheckmarkIcon, CrossIcon, CurecallTooltip } from '@honestica/curecall-ui'
import { ProjectService } from 'src/services/project.service'
import { useCurrentProject } from 'src/providers/current-project.provider'
import {
  EInteropV2DecisionAction,
  TInteropV2Decision,
  TTestInteropMappingResponse,
} from './interop-mapping.type'

export type TInteropTestMappingResultComponentProps = {
  result: TTestInteropMappingResponse
}

const headerCellClassName = 'c-px-2 c-py-1 c-text-left c-font-bold c-border-b c-border-grey-500'
const cellClassName = 'c-px-2 c-py-1 c-border-b c-border-grey-300'

export function InteropTestMappingResultComponent({ result }: TInteropTestMappingResultComponentProps) {
  const { t } = useTranslation()
  const { project, getFollowupById, getMedEventLabelById } = useCurrentProject()
  const { data: interopMappings } = useQuery({
    queryKey: ['interop-mappings', project.id],
    queryFn: () => ProjectService.getInteropMappings(project.id),
  })

  const { legacyMapping, v2Decisions } = result

  const getDecisionMappingLabel = (decision: TInteropV2Decision) => {
    if (decision.InteropMappingId == null) {
      return t('components.patient.interop-logs.actions.test-result-nomap')
    }
    const mapping = interopMappings?.find((interopMapping) => interopMapping.id === decision.InteropMappingId)
    return `${decision.InteropMappingId}${mapping?.description ? ` - ${mapping.description}` : ''}`
  }

  return (
    <div className="c-flex c-flex-col c-gap-2 c-mb-2">
      <div className="c-flex c-gap-1 c-items-center">
        <span className="c-font-bold">
          {t('components.patient.interop-logs.actions.test-result-legacy')}
          {' :'}
        </span>
        {legacyMapping ? (
          <>
            <CheckmarkIcon className="c-text-green-500 c-h-4 c-w-4" />
            {`${legacyMapping.id} - ${legacyMapping.description}`}
          </>
        ) : (
          <>
            <CrossIcon className="c-text-red-500 c-h-4 c-w-4" />
            {t('components.patient.interop-logs.actions.test-result-no-legacy')}
          </>
        )}
      </div>
      <div className="c-font-bold">
        {t('components.patient.interop-logs.actions.test-result-v2')}
        {' :'}
      </div>
      {v2Decisions.length === 0 ? (
        <div className="c-flex c-gap-1 c-items-center">
          <CrossIcon className="c-text-red-500 c-h-4 c-w-4" />
          {t('components.patient.interop-logs.actions.test-result-no-v2')}
        </div>
      ) : (
        <table className="c-w-full c-border-collapse c-text-sm">
          <thead>
            <tr>
              <th className={headerCellClassName}>
                {t('components.patient.interop-logs.actions.test-result-col-action')}
              </th>
              <th className={headerCellClassName}>
                {t('components.patient.interop-logs.actions.test-result-col-mapping')}
              </th>
              <th className={headerCellClassName}>
                {t('components.patient.interop-logs.actions.test-result-col-followup')}
              </th>
              <th className={headerCellClassName}>
                {t('components.patient.interop-logs.actions.test-result-col-med-event')}
              </th>
              <th className={headerCellClassName}>
                {t('components.patient.interop-logs.actions.test-result-col-remote-id')}
              </th>
            </tr>
          </thead>
          <tbody>
            {v2Decisions.map((decision, index) => (
              <tr key={index}>
                <td className={cellClassName}>
                  {decision.action === EInteropV2DecisionAction.CANCEL ? (
                    <CurecallTooltip
                      content={t('components.patient.interop-logs.actions.test-result-cancel')}
                      tooltipPosition="bottom-start"
                    >
                      <span className="c-font-bold">{decision.action}</span>
                    </CurecallTooltip>
                  ) : (
                    <span className="c-font-bold">{decision.action}</span>
                  )}
                </td>
                <td className={cellClassName}>{getDecisionMappingLabel(decision)}</td>
                <td className={cellClassName}>
                  {decision.followupId != null
                    ? (getFollowupById(decision.followupId)?.name ?? decision.followupId)
                    : '-'}
                </td>
                <td className={cellClassName}>
                  {decision.medEventId != null
                    ? getMedEventLabelById(decision.medEventId) || decision.medEventId
                    : '-'}
                </td>
                <td className={cellClassName}>
                  <code className="c-font-mono">{decision.remoteId}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!!legacyMapping !== v2Decisions.length > 0 && (
        <div className="c-text-orange-500">
          {legacyMapping
            ? t('components.patient.interop-logs.actions.test-result-mismatch-legacy')
            : t('components.patient.interop-logs.actions.test-result-mismatch-v2')}
        </div>
      )}
    </div>
  )
}
